/**
 * Servicios API para gestión de áreas y filtros RBAC
 * Sistema de Áreas y Filtros - Fase 1 y 2
 */

import { config } from '@/config/env'
import type {
  AssignUserAreaResponse,
  AssignAreaEncargadoResponse,
  GetAreaWithRelationsResponse,
  FilterJobRequestParams,
  PaginatedJobRequestsResponse,
  FilterHrInterviewParams,
  PaginatedHrInterviewsResponse,
  UserWithArea
} from '@/types/filters.types'

// Create fetch wrapper with auth
const apiRequest = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('access_token')
  
  const response = await fetch(`${config.apiBaseUrl}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    }
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    
    // 🔐 MANEJO DE TOKEN EXPIRADO
    if (response.status === 401) {
      console.error('🚨 [Area Service] TOKEN EXPIRADO - Emitiendo evento de sesión no autorizada')
      
      // Limpiar token
      localStorage.removeItem('access_token')
      
      // Emitir evento para que el hook useAuth lo capture
      window.dispatchEvent(new CustomEvent('auth:unauthorized', {
        detail: { message: errorData.message || 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.' }
      }))
    }
    
    throw {
      response: {
        status: response.status,
        data: errorData
      },
      message: errorData.message || `HTTP ${response.status}`
    }
  }

  return response.json()
}

// ==================== CATÁLOGO DE ÁREAS ====================

export interface Area {
  id: number
  nombre: string
  descripcion?: string
  encargadoId?: string
  activa: boolean
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

/**
 * Obtener listado de todas las áreas disponibles
 */
export const getAreas = async (): Promise<Area[]> => {
  return apiRequest<Area[]>('/catalogos/areas')
}

/**
 * Obtener catálogo de áreas (requiere JWT, sin permisos de catálogo)
 */
export const getAreasCatalog = async (): Promise<Area[]> => {
  return apiRequest<Area[]>('/catalogos/areas/public/all')
}

// ==================== GESTIÓN DE USUARIOS Y ÁREAS ====================

/**
 * Asignar o quitar área a un usuario
 * @param userId - UUID del usuario
 * @param areaId - ID del área (number) o null para quitar
 */
export const assignAreaToUser = async (
  userId: string,
  areaId: number | null
): Promise<AssignUserAreaResponse> => {
  return apiRequest<AssignUserAreaResponse>(`/users/${userId}/area`, {
    method: 'PATCH',
    body: JSON.stringify({ areaId })
  })
}

/**
 * Obtener usuario con información de área
 * @param userId - UUID del usuario
 */
export const getUserWithArea = async (userId: string): Promise<UserWithArea> => {
  return apiRequest<UserWithArea>(`/users/${userId}/with-area`)
}

// ==================== GESTIÓN DE ÁREAS Y ENCARGADOS ====================

/**
 * Asignar o quitar encargado a un área
 * @param areaId - ID del área
 * @param encargadoId - UUID del usuario o null para quitar
 */
export const assignAreaManager = async (
  areaId: number,
  encargadoId: string | null
): Promise<AssignAreaEncargadoResponse> => {
  return apiRequest<AssignAreaEncargadoResponse>(
    `/catalogos/areas/${areaId}/encargado`,
    {
      method: 'PATCH',
      body: JSON.stringify({ encargadoId })
    }
  )
}

/**
 * Obtener área con todas sus relaciones (encargado y usuarios)
 * @param areaId - ID del área
 */
export const getAreaWithRelations = async (
  areaId: number
): Promise<GetAreaWithRelationsResponse> => {
  return apiRequest<GetAreaWithRelationsResponse>(
    `/catalogos/areas/${areaId}/with-relations`
  )
}

// ==================== FILTROS DE JOB REQUESTS ====================

/**
 * Obtener job requests filtradas por área y otros criterios
 * 
 * 🔄 ACTUALIZADO: 29 Oct 2025
 * Comportamiento por rol:
 * - PROJECT_MANAGER/SUPERVISOR (con área): Ve solo su área, backend ignora areaId
 * - PROJECT_MANAGER/SUPERVISOR (sin área): Error 400
 * - ADMIN_RH/ADMIN: Ve todas, puede filtrar por areaId
 * 
 * @param filters - Parámetros de filtrado
 */
export const getFilteredJobRequests = async (
  filters: FilterJobRequestParams
): Promise<PaginatedJobRequestsResponse> => {
  const params = new URLSearchParams()
  
  if (filters.areaId !== undefined) {
    params.append('areaId', filters.areaId.toString())
  }
  if (filters.status) {
    params.append('status', filters.status)
  }
  if (filters.page) {
    params.append('page', filters.page.toString())
  }
  if (filters.limit) {
    params.append('limit', filters.limit.toString())
  }
  
  return apiRequest<PaginatedJobRequestsResponse>(
    `/job-requests/filtered?${params.toString()}`
  )
}

// ==================== FILTROS DE HR INTERVIEWS ====================

/**
 * Obtener HR interviews filtradas por área y otros criterios
 * 
 * 🔄 ACTUALIZADO: 29 Oct 2025
 * Comportamiento por rol:
 * - PROJECT_MANAGER/SUPERVISOR (con área): Ve solo su área, backend ignora areaId
 * - ADMIN_RH/RECLUTADOR_RH: Ve todas, puede filtrar por areaId
 * 
 * @param filters - Parámetros de filtrado
 */
export const getFilteredHrInterviews = async (
  filters: FilterHrInterviewParams
): Promise<PaginatedHrInterviewsResponse> => {
  const params = new URLSearchParams()
  
  if (filters.areaId !== undefined) {
    params.append('areaId', filters.areaId.toString())
  }
  if (filters.decision) {
    params.append('decision', filters.decision)
  }
  if (filters.pendingOnly !== undefined) {
    params.append('pendingOnly', filters.pendingOnly.toString())
  }
  if (filters.page) {
    params.append('page', filters.page.toString())
  }
  if (filters.limit) {
    params.append('limit', filters.limit.toString())
  }
  
  return apiRequest<PaginatedHrInterviewsResponse>(
    `/candidates/hr-interview/filtered?${params.toString()}`
  )
}

// ==================== HELPERS ====================

/**
 * Verificar si el error es de área no asignada
 * @param error - Error de fetch/api
 */
export const isAreaNotAssignedError = (error: any): boolean => {
  return (
    error.response?.status === 400 &&
    (error.response?.data?.message?.includes('sin área') ||
      error.response?.data?.message?.includes('no tiene un área'))
  )
}

/**
 * Verificar si el error es de permisos insuficientes
 * @param error - Error de fetch/api
 */
export const isPermissionError = (error: any): boolean => {
  return error.response?.status === 403
}

/**
 * Obtener mensaje de error amigable
 * @param error - Error de fetch/api
 */
export const getErrorMessage = (error: any): string => {
  if (isAreaNotAssignedError(error)) {
    return 'Tu usuario no tiene un área asignada. Contacta al administrador.'
  }
  
  if (isPermissionError(error)) {
    return 'No tienes permisos para realizar esta acción.'
  }
  
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  
  if (error.message) {
    return error.message
  }
  
  return 'Ocurrió un error inesperado.'
}
